import React, { useState, useEffect, useMemo } from "react";
import Header from "../../components/header/Header";
import Sidebar from "../../components/sidebar/Sidebar";
import { useMediaQuery } from "react-responsive";
import "./StudentHome.css";
import { useAuth } from "../../context/AuthContext";
import TutorCard from "../../components/TutorCard/TutorCard";
import api from "../../utils/api";
import BackToTop from "../../components/backToTop/BackToTop";
import TutorCardSkeleton from "../../components/TutorCard/TutorCardSkeleton";

const StudentHome = () => {
  const { user } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);

  const [tutors, setTutors] = useState([]);

  // Data States
  const [providers, setProviders] = useState([]); // All tutors + centers
  const [allSubjects, setAllSubjects] = useState([]); // Unique subjects for dropdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 });
  const isDesktop = useMediaQuery({ minWidth: 1025 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(4);

  // Fetch providers from backend
  // useEffect(() => {
  //   const fetchProviders = async () => {
  //     try {
  //       setLoading(true);
  //       const response = await api.get("/users/tutors-academies");
  //       const data = response.data || [];
  //       console.log("providers", data);
  //       setProviders(data);

  //       // Extract unique subjects from selectedCourses array
  //       const subjectSet = new Set();
  //       data.forEach((p) => {
  //         if (Array.isArray(p.selectedCourses)) {
  //           p.selectedCourses.forEach((subj) => subjectSet.add(subj));
  //         }
  //       });
  //       setAllSubjects(["all", ...Array.from(subjectSet).sort()]);

  //       setLoading(false);
  //     } catch (err) {
  //       console.error("Error fetching providers:", err);
  //       setError("Failed to load tutors and centers");
  //       setLoading(false);
  //     }
  //   };

  //   fetchProviders();
  // }, []);

  // Fetch providers + subjects
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch providers (paginated)
        const params = new URLSearchParams({
          page: currentPage,
          limit: itemsPerPage,
          ...(searchQuery && { q: searchQuery.trim() }),
          ...(selectedSubject !== "all" && { subject: selectedSubject }),
        });

        const res = await api.get(`/users/tutors-academies?${params.toString()}`);

        const { data, total: totalCount, pages: totalPages } = res.data;

        setProviders(data || []);
        setTotal(totalCount || 0);
        setPages(totalPages || 1);

        // 2. Fetch all subjects only once (can be cached / moved to separate effect)
        if (allSubjects.length === 0) {
          // Option A: extract from current page (fast but incomplete)
          const subjectSet = new Set();
          data.forEach((p) => {
            if (Array.isArray(p.selectedCourses)) {
              p.selectedCourses.forEach((s) => subjectSet.add(s));
            }
          });
          setAllSubjects(["all", ...Array.from(subjectSet).sort()]);

          // Option B: better – separate endpoint that returns all unique subjects
          // const subjRes = await api.get("/users/subjects");
          // setAllSubjects(["all", ...subjRes.data.subjects.sort()]);
        }

        setLoading(false);
      } catch (err) {
        console.error("Home fetch error:", err);
        setError("Failed to load tutors and centers");
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, searchQuery, selectedSubject, itemsPerPage]);

  // Filter logic (search + subject)
  const filteredProviders = useMemo(() => {
    return providers.filter((provider) => {
      const matchesSearch =
        searchQuery === "" ||
        provider.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        provider.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        provider.academyName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        provider.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (Array.isArray(provider.selectedCourses) &&
          provider.selectedCourses.some((s) =>
            s.toLowerCase().includes(searchQuery.toLowerCase()),
          ));

      const matchesSubject =
        selectedSubject === "all" ||
        (Array.isArray(provider.selectedCourses) &&
          provider.selectedCourses.includes(selectedSubject));

      return matchesSearch && matchesSubject;
    });
  }, [providers, searchQuery, selectedSubject]);

  // Pagination
  const totalItems = filteredProviders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  // const currentItems = filteredProviders.slice(startIdx, startIdx + itemsPerPage);
  const currentItems = isMobile
    ? filteredProviders.slice(0, mobileVisibleCount)
    : filteredProviders.slice(startIdx, startIdx + itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setMobileVisibleCount(4);
  }, [searchQuery, selectedSubject, itemsPerPage]);

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || "Student";


  if (error) {
    return (
      <div className="tutors-app">
        <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <main className="student-main">
          <div className="student-container">
            <p style={{ color: "red", textAlign: "center", padding: "50px" }}>
              {error}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="tutors-app">
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <main className={`student-main ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="student-container">
          <div className="student-greeting">
            <div>
              <h1>Welcome, {userName}</h1>
              {/* <h2>Explore available tutors and training centers</h2> */}
              <p className="subheading">
                Explore our catalogue of expert tutors and top centers to boost
                your learning.
              </p>
            </div>

            <div className="student-catalogue-controls">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search by name, subject, or center..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="search-btn">Search</button>
              </div>

              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="filter-select"
              >
                {allSubjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject === "all" ? "All Programs" : subject}
                  </option>
                ))}
              </select>

              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="per-page-select"
              >
                <option value={12}>12 per page</option>
                <option value={24}>24 per page</option>
                <option value={36}>36 per page</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          {/* <div className="results-summary">
            <p>
              Showing {currentItems.length} of {totalItems} results
              {searchQuery && ` for "${searchQuery}"`}
              {selectedSubject !== "all" && searchQuery && " "}
              {selectedSubject !== "all" && `in ${selectedSubject}`}
            </p>
          </div> */}

          <div className="results-summary">
            <p>
              Showing {providers.length} of {total} results
              {searchQuery && ` for "${searchQuery}"`}
              {selectedSubject !== "all" && ` in ${selectedSubject}`}
            </p>
          </div>

          {/* <div className="tutors-grid">
            {currentItems.length === 0 ? (
              <div className="no-results">
                <p>No tutors or centers match your search.</p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedSubject("all");
                  }}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              currentItems.map((provider) => (
                <TutorCard key={provider._id} tutor={provider} />
              ))
            )}
          </div> */}

          {/* Main grid with skeletons */}
          <div className="student-tutors-grid">
            {loading ? (
              // Skeleton loading – repeat 6–12 cards depending on screen
              Array.from({ length: isMobile ? 6 : 12 }).map((_, i) => (
                <TutorCardSkeleton key={`skeleton-${i}`} />
              ))
            ) : providers.length === 0 ? (
              <div className="no-results">
                <p>No tutors or centers found.</p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedSubject("all");
                    setCurrentPage(1);
                  }}
                >
                  Clear filters
                </button>
              </div>
            ) : (
              providers.map((provider) => (
                <TutorCard key={provider._id} tutor={provider} />
              ))
            )}
          </div>

          {/* Pagination */}
          {!isMobile && totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>

              <div className="page-numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`page-num ${currentPage === pageNum ? "active" : ""}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span>...</span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className={`page-num ${currentPage === totalPages ? "active" : ""}`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>
            </div>
          )}

        </div>
      </main>
        <BackToTop />
    </div>
  );
};

export default StudentHome;
